/**
 * Tek atama motorunun paylaşılan sabitleri (ADR 2026-10-29-tek-atama-motoru).
 *
 * Motor SAF bir fonksiyondur (`apps/api/src/orders/assignment/assignment-engine.ts`); kararı
 * üreten ile kararı okuyan (panel, mobil) aynı gerekçe sözlüğünü kullanır. Gerekçe iki
 * kolonda durur:
 *   - `Order.assignReason` — sebep (`AssignReason` enum; Prisma ile birebir aynı).
 *   - `Order.assignSource` — karar anındaki KANIT (etiket, kural id, bölge id, HISTORY oyu).
 *
 * Buradaki değerler Prisma enum'u ile BİREBİR aynı olmalıdır; değer değişirse migration
 * gerekir (kolonlar ekleme-only idi).
 */

import { t } from './i18n';

/** Atama gerekçesi — Prisma `AssignReason` enum'u ile birebir. */
export const ASSIGN_REASON = {
  /** Panel etiketi (driverMap) siparişi şoföre bağladı. */
  PANEL_LABEL: 'PANEL_LABEL',
  /** Sokak kuralı. */
  STREET: 'STREET',
  /** Bölge (mahalle) kuralı. */
  REGION: 'REGION',
  /** Geçmiş siparişlerin çoğunluğu. */
  HISTORY: 'HISTORY',
  /** Elle atama (kişi aracı verdi). */
  MANUAL: 'MANUAL',
  /** Atanamadı — `assignSource` neden kodunu taşır. */
  UNMATCHED: 'UNMATCHED',
} as const;

export type AssignReason = (typeof ASSIGN_REASON)[keyof typeof ASSIGN_REASON];

/** Gerekçe enum'unun tüm değerleri (test ve doğrulama için). */
export const ASSIGN_REASONS: readonly AssignReason[] = Object.values(ASSIGN_REASON);

/**
 * `UNMATCHED` gerekçesinin alt kodu — `assignSource` alanına yazılır.
 *
 * Kodlar karar tablosunun satırlarına BİREBİR karşılık gelir (tasarım §4). Metinler
 * `packages/shared/src/i18n/tr.ts` içindedir; kod burada sabittir.
 */
export const ASSIGN_UNMATCHED = {
  /** Etiket eşleme deposunda yok (Hayat'ta: plaka hiçbir araca uymuyor). */
  UNMAPPED: 'UNMAPPED',
  /** Etiket eşlenmiş ama şoför/araç silinmiş (bayat eşleme). */
  STALE_MAP: 'STALE_MAP',
  /** Şoför (ya da kullanıcısı) pasif. */
  DRIVER_INACTIVE: 'DRIVER_INACTIVE',
  /** Şoförün aracı yok (ya da aracı pasif). */
  NO_VEHICLE: 'NO_VEHICLE',
  /** Araç başka şoförün açık seferinde (yalnız Abant). */
  VEHICLE_BUSY: 'VEHICLE_BUSY',
  /** Açık sefer bilgisi karar anında bayat (sefer değişmiş) — yazma yolu yeniden denetler. */
  STALE_TRIP: 'STALE_TRIP',
  /** Çıkarım kapalı (atama modu MANUAL). */
  MODE_MANUAL: 'MODE_MANUAL',
  /** Adres birden çok sokak kuralına uyuyor. */
  AMBIGUOUS_STREET: 'AMBIGUOUS_STREET',
  /** Sokak bölünmüş (yan/kapı no parçaları) ve adreste kapı no yok: araç atanmaz. */
  STREET_NO_NUMBER: 'STREET_NO_NUMBER',
  /** Sokak kuralının hedefi (araç/bölge) pasif ya da yok. */
  RULE_TARGET_INACTIVE: 'RULE_TARGET_INACTIVE',
  /** Bölge eşleşti ama otomatik atama kapalı / araç yok / araç pasif. */
  REGION_NO_VEHICLE: 'REGION_NO_VEHICLE',
  /** Geçmiş için anahtar (mahalle/sokak) çıkarılamadı. */
  HIST_NO_KEY: 'HIST_NO_KEY',
  /** Geçmiş oyu yetersiz (en az 5 oy). */
  HIST_THIN: 'HIST_THIN',
  /** Geçmiş oyu bölünmüş (pay %70'in altında). */
  HIST_SPLIT: 'HIST_SPLIT',
  /** Geçmişin kazananı araç/şoför eşlemesiyle çözülemedi. */
  HIST_UNMAPPED: 'HIST_UNMAPPED',
  /** Geçmiş sağlayıcısı yok (geçmiş yüklenmedi) — "yetersiz oy" gibi görünmez. */
  HIST_NOT_LOADED: 'HIST_NOT_LOADED',
  /** Tek sinyal koordinat; metin ve kural yok. */
  COORD_ONLY: 'COORD_ONLY',
  /** Uygulanacak kural yok (adres de yok). */
  NO_RULE: 'NO_RULE',
} as const;

export type AssignUnmatchedCode = (typeof ASSIGN_UNMATCHED)[keyof typeof ASSIGN_UNMATCHED];

/** Tüm UNMATCHED kodları (test ve doğrulama için). */
export const ASSIGN_UNMATCHED_CODES: readonly AssignUnmatchedCode[] =
  Object.values(ASSIGN_UNMATCHED);

/**
 * GÖREVLENDİRME UCUNUN 409 KODLARI — `UNMATCHED` alt kodu DEĞİLDİR.
 *
 * Bu kodlar "sipariş atanamadı" demek DEĞİLDİR: görevlendirme isteğinin KENDİSİ reddedilir
 * (gün kilitli, tekillik ihlali). Karışmaması için `ASSIGN_UNMATCHED`'dan AYRI bir sabitte
 * tutulur — `assignSource`'a yazılan alt kod her zaman "atama nedeni"dir.
 */
export const ASSIGN_DAY_ERROR = {
  /** Görevlendirme geçmiş gün için yazılamaz (gün kilitli). */
  PAST_DAY_LOCKED: 'PAST_DAY_LOCKED',
  /** O gün şoförün başka bir görevlendirmesi var (tekillik ihlali). */
  DAY_DRIVER_TAKEN: 'DAY_DRIVER_TAKEN',
  /** O gün aracın başka bir şoförü var (tekillik ihlali). */
  DAY_VEHICLE_TAKEN: 'DAY_VEHICLE_TAKEN',
} as const;

export type AssignDayErrorCode = (typeof ASSIGN_DAY_ERROR)[keyof typeof ASSIGN_DAY_ERROR];

/** Tüm görevlendirme hata kodları (test ve doğrulama için). */
export const ASSIGN_DAY_ERROR_CODES: readonly AssignDayErrorCode[] =
  Object.values(ASSIGN_DAY_ERROR);

/**
 * GÜNÜN ŞOFÖRÜ kaynağı (rozet) — eşleşme hangi katmandan geldi?
 *
 * `trip` = aracın açık seferi · `assignment` = günlük görevlendirme · `default` = zimmet.
 */
export const ASSIGN_ROSTER_SOURCE = {
  /** (a) aracın açık seferi. */
  TRIP: 'trip',
  /** (b) günlük görevlendirme. */
  ASSIGNMENT: 'assignment',
  /** (c) zimmet (varsayılan). */
  DEFAULT: 'default',
} as const;

export type AssignRosterSource = (typeof ASSIGN_ROSTER_SOURCE)[keyof typeof ASSIGN_ROSTER_SOURCE];

/**
 * Atama olayı türleri (`OrderEvent.type` — String kolon).
 *
 * `ROSTER_CHANGED`: günün şoför rosteri değişti (görevlendirme yazıldı/silindi). O günün
 * atanmış siparişleri yeniden değerlendirilir; araç değişirse `ROSTER_CHANGED` yazılır.
 */
export const ASSIGN_EVENT = {
  /** Panel etiketi eşlemesi değişti. */
  PANEL_LABEL_CHANGED: 'PANEL_LABEL_CHANGED',
  /** Günün şoför rosteri değişti (görevlendirme). */
  ROSTER_CHANGED: 'ROSTER_CHANGED',
} as const;

export type AssignEvent = (typeof ASSIGN_EVENT)[keyof typeof ASSIGN_EVENT];

/** Tüm atama olayı türleri (test ve doğrulama için). */
export const ASSIGN_EVENTS: readonly AssignEvent[] = Object.values(ASSIGN_EVENT);

/**
 * Bir gerekçenin kullanıcıya görünen Türkçe metni.
 * `assignReason` kolonu panelde rozet olarak gösterilir.
 */
export function assignReasonText(reason: AssignReason): string {
  return t(`assign.reason.${reason}`);
}

/** Bir UNMATCHED kodunun kullanıcıya görünen Türkçe metni. */
export function assignUnmatchedText(code: AssignUnmatchedCode): string {
  return t(`assign.unmatched.${code}`);
}

/** Bir görevlendirme hata kodunun (409) kullanıcıya görünen Türkçe metni. */
export function assignDayErrorText(code: AssignDayErrorCode): string {
  return t(`assign.dayError.${code}`);
}

/** Roster kaynak rozetinin (hangi katmandan geldi) Türkçe metni. */
export function rosterSourceText(source: AssignRosterSource): string {
  return t(`assign.roster.source.${source}`);
}
